import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { ref, getDownloadURL } from 'firebase/storage';
import { functions, storage } from '../../firebase/config';
import { DOCUMENT_TYPES, REQUIRED_KEYS, documentState, languageLabel } from '../../utils/driverDocuments';
import { COLORS } from '../../styles/tokens';

const STATE_CHIPS = {
  approved: { label: 'Godkjent',        style: { background: COLORS.successBg, color: COLORS.successText } },
  pending:  { label: 'Til godkjenning', style: { background: COLORS.warningBg, color: COLORS.warningText } },
  missing:  { label: 'Mangler',         style: { background: COLORS.surfaceHover, color: COLORS.textLight } },
  rejected: { label: 'Avvist',          style: { background: COLORS.dangerBg,  color: COLORS.dangerText } },
  expired:  { label: 'Utløpt',          style: { background: COLORS.dangerBg,  color: COLORS.dangerText } },
};

// Vinduet åpnes synkront ved klikk (ellers blokkerer nettleseren popup etter await), og URL-en settes når den er hentet.
async function openFile(path) {
  const w = window.open('', '_blank');
  if (w) w.opener = null;
  try {
    const url = await getDownloadURL(ref(storage, path));
    if (w) w.location.href = url;
    else window.open(url, '_blank', 'noopener');
  } catch (e) {
    if (w) w.close();
    throw e;
  }
}

function DocRow({ uid, title, doc, docKey, courseId, onChanged }) {
  const state = documentState(doc);
  const chip = STATE_CHIPS[state];
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function review(decision, extra = {}) {
    setBusy(true); setError('');
    try {
      const payload = { uid, docKey, ...(courseId ? { courseId } : {}), decision, ...extra };
      await httpsCallable(functions, 'reviewDriverDocument')(payload);
      setRejecting(false); setReason('');
      onChanged?.();
    } catch (e) {
      setError(e?.message || 'Kunne ikke lagre. Prøv igjen.');
    } finally {
      setBusy(false);
    }
  }

  function sendReject() {
    if (!reason.trim()) return setError('Skriv en begrunnelse.');
    review('reject', { reason: reason.trim() });
  }

  const files = Array.isArray(doc?.files) ? doc.files : [];

  return (
    <div style={styles.row}>
      <div style={styles.rowHead}>
        <span style={styles.title}>{title}</span>
        <span style={{ ...styles.chip, ...chip.style }}>{chip.label}</span>
      </div>
      {doc?.expiresOn && <div style={styles.meta}>Utløper {doc.expiresOn}</div>}
      {state === 'rejected' && doc.rejectReason && <div style={{ ...styles.meta, color: COLORS.dangerText }}>Avvist: {doc.rejectReason}</div>}
      {files.length > 0 && (
        <div style={styles.files}>
          {files.map(f => (
            <button key={f.path} type="button" aria-label={`Åpne ${f.name}`} style={styles.link}
                    onClick={() => openFile(f.path).catch(() => setError('Kunne ikke åpne filen.'))}>
              {f.name}
            </button>
          ))}
        </div>
      )}
      {state === 'pending' && !rejecting && (
        <div style={styles.actions}>
          <button type="button" aria-label={`Godkjenn ${title}`} disabled={busy} style={{ ...styles.btn, background: COLORS.success }}
                  onClick={() => review('approve')}>Godkjenn</button>
          <button type="button" aria-label={`Avvis ${title}`} disabled={busy} style={{ ...styles.btn, background: COLORS.danger }}
                  onClick={() => { setRejecting(true); setError(''); }}>Avvis</button>
        </div>
      )}
      {rejecting && (
        <div style={{ marginTop: 8 }}>
          <label style={styles.meta}>
            Begrunnelse
            <textarea rows={2} value={reason} style={styles.textarea} onChange={e => setReason(e.target.value)} />
          </label>
          <div style={styles.actions}>
            <button type="button" disabled={busy} style={{ ...styles.btn, background: COLORS.danger }} onClick={sendReject}>Send avvisning</button>
            <button type="button" disabled={busy} style={{ ...styles.btn, background: COLORS.textLight }}
                    onClick={() => { setRejecting(false); setReason(''); setError(''); }}>Avbryt</button>
          </div>
        </div>
      )}
      {error && <div style={{ ...styles.meta, color: COLORS.dangerText }}>{error}</div>}
    </div>
  );
}

export default function DriverDocumentsAdmin({ user, onChanged }) {
  const documents = user.documents ?? {};
  const courses = Array.isArray(documents.courses) ? documents.courses : [];
  const languages = Array.isArray(user.languages) ? user.languages : [];

  return (
    <div>
      {REQUIRED_KEYS.map(key => (
        <DocRow key={key} uid={user.id} docKey={key} title={DOCUMENT_TYPES[key].label}
                doc={documents[key]} onChanged={onChanged} />
      ))}
      {courses.map(c => (
        <DocRow key={c.id} uid={user.id} docKey="course" courseId={c.id} title={c.name || 'Kurs'}
                doc={c} onChanged={onChanged} />
      ))}
      <div style={{ ...styles.meta, marginTop: 8 }}>
        Språk: {languages.length ? languages.map(languageLabel).join(', ') : '—'}
      </div>
    </div>
  );
}

const styles = {
  row:      { padding: '10px 12px', background: COLORS.surfaceHover, borderRadius: 8, marginBottom: 8 },
  rowHead:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  title:    { fontWeight: 600, fontSize: 14, color: COLORS.textDark },
  chip:     { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, flexShrink: 0 },
  meta:     { fontSize: 12, color: COLORS.textLight, marginTop: 4 },
  files:    { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 },
  link:     { background: 'none', border: 'none', padding: 0, color: COLORS.blue, textDecoration: 'underline', cursor: 'pointer', fontSize: 13 },
  actions:  { display: 'flex', gap: 8, marginTop: 8 },
  btn:      { padding: '6px 14px', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  textarea: { display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 4, padding: 8, borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13, background: COLORS.white, color: COLORS.textDark },
};
